/**
 * @fileoverview Company Management React Query Hooks
 *
 * Provides React Query hooks for company CRUD operations and analytics.
 * Integrates with CompanyService for backend communication and handles
 * automatic cache invalidation and optimistic updates.
 *
 * @module hooks/useCompanies
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@xanectech/ui/lib';
import { useRole } from '@xanectech/auth';
import { CompanyService } from '../services/CompanyService';
import type { Company } from '@xanectech/types';
import { createLogger } from '@xanectech/lib';
const logger = createLogger('Data - useCompanies');

/**
 * Hook to fetch all companies (requires system admin permissions)
 *
 * Retrieves the complete list of companies in the system.
 * Access is controlled by role-based permissions.
 *
 * @returns {UseQueryResult<Company[]>} React Query result with companies data
 *
 * @example
 * ```tsx
 * function CompanyList() {
 *   const { data: companies, isLoading, error } = useCompanies();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return (
 *     <ul>
 *       {companies?.map(company => (
 *         <li key={company.id}>{company.name}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useCompanies = () => {
  const { hasPermission } = useRole();

  return useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.getAllCompanies(
        hasPermission('canManageCompanies') as boolean,
      ),
  });
};

/**
 * Hook to fetch company statistics and metrics
 *
 * Retrieves aggregated statistics about all companies in the system
 * (total count, active companies, etc.)
 *
 * @returns {UseQueryResult} React Query result with company statistics
 *
 * @example
 * ```tsx
 * function CompanyDashboard() {
 *   const { data: stats } = useCompanyStats();
 *
 *   return (
 *     <div>
 *       <h2>Total Companies: {stats?.total}</h2>
 *       <h2>Active: {stats?.active}</h2>
 *     </div>
 *   );
 * }
 * ```
 */
export const useCompanyStats = () => {
  return useQuery({
    queryKey: ['company-stats'],
    queryFn: CompanyService.getCompanyStats,
  });
};

/**
 * Hook to fetch a single company by ID (requires company admin permissions)
 *
 * Retrieves detailed information for a specific company.
 * Query is only executed when companyId is provided.
 *
 * @param {string} [companyId] - The unique identifier of the company
 * @returns {UseQueryResult<Company>} React Query result with company data
 *
 * @example
 * ```tsx
 * function CompanyDetails({ id }: { id: string }) {
 *   const { data: company, isLoading } = useCompany(id);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       <h1>{company?.name}</h1>
 *       <p>{company?.description}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useCompany = (companyId?: string) => {
  const { hasPermission } = useRole();

  return useQuery({
    queryKey: ['company', companyId],
    queryFn: () => {
      if (!companyId) throw new Error('Company ID is required');
      return CompanyService.getCompany(
        companyId,
        hasPermission('canManageOwnCompany') as boolean,
      );
    },
    enabled: !!companyId,
  });
};

/**
 * Hook to create a new company
 *
 * Mutation hook for creating a new company record.
 * Automatically invalidates company-related queries and shows success/error toasts.
 *
 * @returns {UseMutationResult} React Query mutation for creating companies
 *
 * @example
 * ```tsx
 * function CreateCompanyForm() {
 *   const createCompany = useCreateCompany();
 *
 *   const handleSubmit = async (formData) => {
 *     await createCompany.mutateAsync({
 *       name: formData.name,
 *       description: formData.description,
 *       // ... other fields
 *     });
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {// form fields}
 *     </form>
 *   );
 * }
 * ```
 */
export const useCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: CompanyService.createCompany,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      void queryClient.invalidateQueries({ queryKey: ['company-stats'] });
      toast({
        title: 'Company Created',
        description: 'New company has been successfully created.',
      });
    },
    onError: (error) => {
      logger.error('Failed to create company', { error });
      toast({
        title: 'Error',
        description: `Failed to create company: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

// Hook to update company
export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Company> }) =>
      CompanyService.updateCompany(id, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({
        title: 'Company Updated',
        description: 'Company information has been successfully updated.',
      });
    },
    onError: (error) => {
      logger.error('Failed to update company', { error });
      toast({
        title: 'Error',
        description: `Failed to update company: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

// Hook to delete company
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: CompanyService.deleteCompany,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      void queryClient.invalidateQueries({ queryKey: ['company-stats'] });
      toast({
        title: 'Company Deleted',
        description: 'Company has been successfully deleted.',
      });
    },
    onError: (error) => {
      logger.error('Failed to delete company', { error });
      toast({
        title: 'Error',
        description: `Failed to delete company: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to search for active companies by name or company code
 *
 * @param {Object} options - Search options
 * @param {string} options.query - The search query string
 * @param {number} [options.limit=15] - Maximum number of results to return
 * @param {boolean} [options.enabled=true] - Whether the query should be enabled
 * @returns {UseQueryResult<Company[]>} React Query result with matching companies
 *
 * @example
 * ```tsx
 * function CompanySearch() {
 *   const [searchTerm, setSearchTerm] = useState('');
 *   const { data: companies, isLoading } = useCompanySearch({
 *     query: searchTerm,
 *     limit: 10
 *   });
 *
 *   return (
 *     <div>
 *       <input
 *         value={searchTerm}
 *         onChange={(e) => setSearchTerm(e.target.value)}
 *       />
 *       {companies?.map(company => (
 *         <div key={company.id}>{company.name}</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useCompanySearch = ({
  query,
  limit = 15,
  enabled = true,
}: {
  query: string;
  limit?: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ['companies-search', query, limit],
    queryFn: () => CompanyService.searchCompanies(query, limit),
    enabled: enabled && query.trim().length > 0,
  });
};
